<template>
  <div class="chat-workspace">
    <aside :class="['history-panel', { open: historyPanelOpen }]">
      <div class="sidebar-brand">
        <div class="sidebar-heading">
          <span class="brand-mark" aria-hidden="true">
            <svg viewBox="0 0 28 28" width="23" height="23">
              <path d="M4 20h20M8 17a6 6 0 0 1 12 0M14 4v3M4.8 8.8l2.1 2.1M23.2 8.8l-2.1 2.1M8 24h12" />
            </svg>
          </span>
          <h1>特教求索</h1>
        </div>
        <p>让 AI 成为特教人最得力的伙伴</p>
      </div>

      <button
        class="new-chat-button"
        @click="startNewConversation"
        :disabled="isStreaming"
      >
        <svg viewBox="0 0 24 24" width="18" height="18">
          <path fill="currentColor" d="M11 5h2v6h6v2h-6v6h-2v-6H5v-2h6z" />
        </svg>
        新对话
      </button>

      <div
        v-if="migrationNotice"
        class="migration-notice"
        :class="migrationState"
      >
        {{ migrationNotice }}
      </div>

      <div class="history-label">最近对话</div>
      <div class="history-list">
        <div v-if="historyLoading" class="history-empty">正在加载历史...</div>
        <div v-else-if="conversationList.length === 0" class="history-empty">
          暂无后端历史
        </div>
        <template v-else>
          <button
            v-for="conversation in conversationList"
            :key="conversation._id"
            :class="[
              'history-item',
              { active: conversation._id === activeConversationId },
            ]"
            @click="selectConversation(conversation._id)"
          >
            <span class="history-title">{{
              getConversationTitle(conversation)
            }}</span>
            <span class="history-meta">{{
              formatTime(conversation.updatedAt || conversation.createdAt)
            }}</span>
          </button>
        </template>
      </div>

      <div class="sidebar-footer">
        <a href="https://tejiao123.com" class="agent-link">
          更多特教智能体 &gt;
        </a>

        <div class="account-area">
          <div v-if="isAuthenticated" class="account-row" :title="userName">
            <span class="account-name">管理员</span>
            <button class="logout-inline-button" @click="$emit('logout')">
              退出登录
            </button>
          </div>
          <button v-else class="account-trigger" @click="$emit('login')">
            登录
          </button>
        </div>
      </div>
    </aside>

    <section class="chat-container">
      <button
        class="mobile-sidebar-button"
        aria-label="打开侧栏"
        @click="historyPanelOpen = true"
      >
        <svg viewBox="0 0 24 24" width="18" height="18">
          <path fill="currentColor" d="M4 5h16v2H4zm0 6h16v2H4zm0 6h10v2H4z" />
        </svg>
      </button>

      <div class="messages-container" ref="messagesContainer">
        <div v-if="messagesLoading" class="center-state">
          <div class="small-spinner"></div>
          <span>正在读取对话...</span>
        </div>

        <div v-else-if="messages.length === 0" class="welcome-container">
          <div class="welcome-title">特教求索</div>
          <div class="welcome-message">
            你好，我是七维教育部署的 AI
            助手特教求索。你可以直接描述学生情况、课堂目标、活动场景或材料需求，我会尽量给出可执行的特殊教育建议。
          </div>
          <div class="example-questions">
            <div class="question-title">可以从这些问题开始：</div>
            <div class="questions-list">
              <button
                v-for="question in exampleQuestions"
                :key="question"
                class="example-question"
                @click="selectQuestion(question)"
              >
                {{ question }}
              </button>
            </div>
          </div>
        </div>

        <div v-else>
          <div
            v-for="message in messages"
            :key="message.id"
            class="message-wrapper"
          >
            <ChatMessage
              :message="message"
              :thinking-time="
                isThinking && message === messages[messages.length - 1]
                  ? thinkingTime
                  : 0
              "
              :is-streaming="
                isStreaming && message === messages[messages.length - 1]
              "
              :hide-loading-dots="
                webSearchEnabled &&
                isStreaming &&
                message === messages[messages.length - 1]
              "
              :interaction-disabled="isStreaming || messagesLoading"
              @ui-submit="sendMessage"
              @ui-state="({ message, state }) => { message.tejiaoUiState = state; }"
              @regenerate="regenerateAiMessage"
            />
          </div>
        </div>
      </div>

      <div class="input-container">
        <FileUploader
          ref="fileUploaderRef"
          :disabled="isStreaming || messagesLoading"
          @update:fileContexts="updateFileContexts"
          @processing="filesProcessing = $event"
        >
          <template #input>
            <div class="input-row">
              <div class="textarea-wrapper">
                <textarea
                  v-model="userInput"
                  @keydown.enter="handleEnter"
                  @compositionstart="handleCompositionStart"
                  @compositionend="handleCompositionEnd"
                  ref="inputTextarea"
                  rows="1"
                  placeholder="向特教求索提问…"
                  aria-label="向特教求索提问"
                  :disabled="isStreaming || messagesLoading"
                ></textarea>
              </div>
            </div>
          </template>

          <template #actions>
            <button
              :class="['search-toggle', { active: webSearchEnabled }]"
              type="button"
              :disabled="isStreaming || messagesLoading"
              @click="webSearchEnabled = !webSearchEnabled"
              :aria-pressed="webSearchEnabled"
            >
              <span class="search-toggle-label">联网搜索</span>
              <span class="search-switch" aria-hidden="true">
                <span></span>
              </span>
            </button>
            <button
              :class="['send-button', { 'stop-button': isStreaming }]"
              @click="isStreaming ? stopResponse() : sendMessage()"
              :disabled="(!canSend && !isStreaming) || messagesLoading"
              :aria-label="isStreaming ? '停止生成' : '发送消息'"
            >
              <svg
                v-if="isStreaming"
                viewBox="0 0 24 24"
                width="22"
                height="22"
              >
                <path fill="currentColor" d="M6.5 6.5h11v11h-11z" />
              </svg>
              <svg v-else viewBox="0 0 24 24" width="20" height="20">
                <path
                  fill="currentColor"
                  d="M12 4 5.7 10.3l1.4 1.4L11 7.83V20h2V7.83l3.9 3.87 1.4-1.4z"
                />
              </svg>
            </button>
          </template>
        </FileUploader>
        <p v-if="filesProcessing || hasFileErrors" class="attachment-status" role="status">
          {{ filesProcessing ? '正在处理附件，请稍候…' : '请移除处理失败的附件后再发送。' }}
        </p>
      </div>

      <Toast ref="toast" />
    </section>

    <div
      v-if="historyPanelOpen"
      class="mobile-history-mask"
      @click="historyPanelOpen = false"
    ></div>
  </div>
</template>

<script setup>
import { computed, nextTick, onMounted, onUnmounted, ref, watch } from "vue";
import { apiService, logChat } from "../utils/api";
import ChatMessage from "./ChatMessage.vue";
import { stripModelProtocolMarkers } from "../utils/modelOutput";
import { buildUserContent, getMessageImages } from "../utils/attachments";
import "../styles/highlight.scss";
import Toast from "./Toast.vue";
import FileUploader from "./FileUploader.vue";

const props = defineProps({
  currentUser: {
    type: Object,
    default: null,
  },
  authError: {
    type: String,
    default: "",
  },
});

const emit = defineEmits(["logout", "login"]);
const PENDING_LOGIN_DRAFT_KEY = "deepseek_tejiao123_pending_login_draft";

const userInput = ref("");
const messages = ref([]);
const conversationList = ref([]);
const activeConversationId = ref("");
const historyLoading = ref(false);
const messagesLoading = ref(false);
const historyPanelOpen = ref(false);
const migrationNotice = ref("");
const migrationState = ref("info");
const isThinking = ref(false);
const thinkingTime = ref(0);
const finalThinkingTime = ref(0);
const messagesContainer = ref(null);
let thinkingTimer = null;
let thinkingStartedAt = 0;
const toast = ref(null);
const inputTextarea = ref(null);
const currentStream = ref(null);
const shouldAutoScroll = ref(true);
const isStreaming = ref(false);
const isInputComposing = ref(false);
const webSearchEnabled = ref(false);
const fileContexts = ref([]);
const filesProcessing = ref(false);
const hasFileErrors = computed(() => fileContexts.value.some((file) => file.status === "error"));
const canSend = computed(() => !filesProcessing.value && !hasFileErrors.value && (
  userInput.value.trim() || fileContexts.value.some((file) => file.status === "done")
));
const fileUploaderRef = ref(null);
const CONTEXT_RETENTION_DAYS = 3;
const CONTEXT_RETENTION_MS = CONTEXT_RETENTION_DAYS * 24 * 60 * 60 * 1000;
const LEGACY_HISTORY_KEY = "chatHistory";
const MIGRATION_KEY_PREFIX = "deepseek_tejiao123_legacy_history_migrated";
const AUTO_SCROLL_BOTTOM_THRESHOLD = 32;
const TYPEWRITER_DELAY_MS = 18;
const TYPEWRITER_FAST_DELAY_MS = 8;
const TYPEWRITER_SPRINT_DELAY_MS = 3;
const TYPEWRITER_FAST_QUEUE_SIZE = 260;
const TYPEWRITER_SPRINT_QUEUE_SIZE = 900;
let pendingAutoScrollFrame = null;
let touchStartY = 0;

const exampleQuestions = [
  "我班上有个中度自闭症的学生，请问在生活课文的课堂教学中，可以给他提供怎样的支持？",
  "我想申请今年的特殊教育示范校，你能给我提供一些课题建议吗？",
  "自闭症日马上到了，你能帮我设计一个自闭症日的活动方案吗？",
];

const isAuthenticated = computed(() => {
  return !!(
    props.currentUser?._id ||
    props.currentUser?.account ||
    props.currentUser?.name
  );
});

const userName = computed(() => {
  return props.currentUser?.name || props.currentUser?.account || "管理员";
});

const openHistoryPanel = () => {
  historyPanelOpen.value = true;
};

defineExpose({
  openHistoryPanel,
});

const selectQuestion = (question) => {
  userInput.value = question;
  nextTick(() => {
    autoResize();
    inputTextarea.value?.focus();
  });
};

const resetUploadedFiles = () => {
  const exposedContexts = fileUploaderRef.value?.fileContexts;
  if (Array.isArray(exposedContexts)) {
    exposedContexts.splice(0, exposedContexts.length);
  } else if (exposedContexts?.value) {
    exposedContexts.value = [];
  }
  fileContexts.value = [];
};

const startThinking = () => {
  isThinking.value = true;
  thinkingTime.value = 0;
  finalThinkingTime.value = 0;
  thinkingStartedAt = Date.now();
  thinkingTimer = setInterval(() => {
    thinkingTime.value = getThinkingElapsedSeconds();
  }, 250);
};

const getThinkingElapsedSeconds = () => {
  if (!thinkingStartedAt) return 0;
  return Math.max(1, Math.ceil((Date.now() - thinkingStartedAt) / 1000));
};

const stopThinkingTimer = (finalize = false) => {
  if (finalize && !finalThinkingTime.value) {
    finalThinkingTime.value = getThinkingElapsedSeconds();
  }
  isThinking.value = false;
  if (thinkingTimer) {
    clearInterval(thinkingTimer);
    thinkingTimer = null;
  }
  thinkingStartedAt = 0;
};

let lastCompositionEndAt = 0;

const handleCompositionStart = () => {
  isInputComposing.value = true;
};

const handleCompositionEnd = () => {
  isInputComposing.value = false;
  lastCompositionEndAt = Date.now();
};

const isCompositionEnter = (e) => {
  return (
    e.isComposing ||
    e.keyCode === 229 ||
    isInputComposing.value ||
    Date.now() - lastCompositionEndAt < 80
  );
};

const handleEnter = (e) => {
  if (e.shiftKey || isCompositionEnter(e)) {
    return;
  }
  e.preventDefault();
  sendMessage();
};

const autoResize = () => {
  const textarea = inputTextarea.value;
  if (!textarea) return;
  // 先收回再测量，删除内容也能缩小；最大高度由 CSS 随可用视口限制。
  textarea.style.height = "0px";
  textarea.style.height = `${textarea.scrollHeight}px`;
};

watch(userInput, () => {
  nextTick(autoResize);
});

const adjustViewportHeight = () => {
  const vh = (window.visualViewport?.height || window.innerHeight) * 0.01;
  document.documentElement.style.setProperty("--vh", `${vh}px`);
  nextTick(autoResize);
};

const handleScroll = () => {
  if (!messagesContainer.value) return;
  shouldAutoScroll.value = isNearBottom();
  if (!shouldAutoScroll.value) {
    cancelPendingAutoScroll();
  }
};

const handleWheel = (event) => {
  if (event.deltaY < 0) {
    shouldAutoScroll.value = false;
    cancelPendingAutoScroll();
  }
};

const handleTouchStart = (event) => {
  touchStartY = event.touches?.[0]?.clientY || 0;
};

const handleTouchMove = (event) => {
  const currentY = event.touches?.[0]?.clientY || 0;
  if (touchStartY && currentY > touchStartY + 6) {
    shouldAutoScroll.value = false;
    cancelPendingAutoScroll();
  }
};

onMounted(async () => {
  autoResize();
  messagesContainer.value?.addEventListener("scroll", handleScroll);
  messagesContainer.value?.addEventListener("wheel", handleWheel, {
    passive: true,
  });
  messagesContainer.value?.addEventListener("touchstart", handleTouchStart, {
    passive: true,
  });
  messagesContainer.value?.addEventListener("touchmove", handleTouchMove, {
    passive: true,
  });
  window.addEventListener("resize", adjustViewportHeight);
  window.visualViewport?.addEventListener("resize", adjustViewportHeight);
  adjustViewportHeight();
  await initializeConversations();
});

watch(
  () =>
    props.currentUser?._id ||
    props.currentUser?.account ||
    props.currentUser?.name ||
    "",
  async (userMark, oldUserMark) => {
    if (userMark && userMark !== oldUserMark) {
      await initializeConversations();
    }
  },
);

onUnmounted(() => {
  window.removeEventListener("resize", adjustViewportHeight);
  window.visualViewport?.removeEventListener("resize", adjustViewportHeight);
  messagesContainer.value?.removeEventListener("scroll", handleScroll);
  messagesContainer.value?.removeEventListener("wheel", handleWheel);
  messagesContainer.value?.removeEventListener("touchstart", handleTouchStart);
  messagesContainer.value?.removeEventListener("touchmove", handleTouchMove);
  cancelPendingAutoScroll();
  stopThinkingTimer();
});

const safeLocalStorageGet = (key) => {
  try {
    return localStorage.getItem(key);
  } catch (error) {
    return "";
  }
};

const safeLocalStorageSet = (key, value) => {
  try {
    localStorage.setItem(key, value);
  } catch (error) {
    // no-op
  }
};

const parseTimestamp = (value) => {
  if (!value) return Date.now();
  if (typeof value === "number") return value;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? Date.now() : date.getTime();
};

const formatTime = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const now = new Date();
  const sameYear = date.getFullYear() === now.getFullYear();
  const dateText = sameYear
    ? `${date.getMonth() + 1}月${date.getDate()}日`
    : `${date.getFullYear()}年${date.getMonth() + 1}月${date.getDate()}日`;
  return `${dateText} ${String(date.getHours()).padStart(2, "0")}:${String(
    date.getMinutes(),
  ).padStart(2, "0")}`;
};

const getConversationTitle = (conversation) => {
  const rawName = `${conversation?.name || ""}`.trim();
  return rawName.replace(/^【特教求索】/, "") || "未命名对话";
};

const normalizeContent = (content) => {
  if (Array.isArray(content)) {
    const textItem = content.find((item) => item?.type === "text");
    return `${textItem?.text || ""}`;
  }
  if (content === undefined || content === null) {
    return "";
  }
  return typeof content === "string" ? content : JSON.stringify(content);
};

const toLocalMessage = (message, index = 0) => {
  const isUser = message.role === "user";
  const timestamp = parseTimestamp(message.createdAt || message.updatedAt);
  const rawContent = isUser
    ? `${message.show_content || normalizeContent(message.content)}`
    : normalizeContent(message.content);
  const rawReasoning = isUser ? "" : `${message.reasoning_content || ""}`;
  return {
    id: message._id || `${message.role}-${timestamp}-${index}`,
    type: isUser ? "user" : "ai",
    content: isUser ? rawContent : stripModelProtocolMarkers(rawContent),
    requestContent: isUser ? message.content : undefined,
    tejiaoUi: isUser ? undefined : message.tejiaoUi,
    tejiaoUiState: isUser ? undefined : message.tejiaoUiState,
    images: isUser ? getMessageImages(message.content) : [],
    reasoning: isUser ? "" : stripModelProtocolMarkers(rawReasoning),
    usedWebSearch: !isUser && rawReasoning.includes("**联网搜索过程**"),
    thinkingTime: 0,
    timestamp,
  };
};

const getLegacyMessages = () => {
  try {
    const rawHistory = safeLocalStorageGet(LEGACY_HISTORY_KEY);
    if (!rawHistory) {
      return [];
    }
    const parsed = JSON.parse(rawHistory);
    if (!Array.isArray(parsed)) {
      return [];
    }
    return parsed
      .filter((item) => ["user", "ai"].includes(item?.type))
      .map((item) => ({
        ...item,
        content:
          item.type === "ai"
            ? stripModelProtocolMarkers(item.content || "")
            : `${item.content || ""}`,
        reasoning:
          item.type === "ai"
            ? stripModelProtocolMarkers(item.reasoning || "")
            : `${item.reasoning || ""}`,
        usedWebSearch:
          item.type === "ai" &&
          `${item.reasoning || ""}`.includes("**联网搜索过程**"),
        timestamp: getMessageTimestamp(item) || Date.now(),
      }))
      .filter((item) => item.content.trim() || item.reasoning.trim());
  } catch (error) {
    console.error("Error reading legacy history:", error);
    return [];
  }
};

const getHistorySignature = (legacyMessages) => {
  const seed = legacyMessages
    .map(
      (item) =>
        `${item.type}:${item.timestamp}:${item.content.slice(
          0,
          80,
        )}:${item.reasoning.slice(0, 40)}`,
    )
    .join("|");
  let hash = 0;
  for (let i = 0; i < seed.length; i += 1) {
    hash = ((hash << 5) - hash + seed.charCodeAt(i)) | 0;
  }
  return `${legacyMessages.length}_${Math.abs(hash).toString(36)}`;
};

const getMigrationStorageKey = (signature) => {
  return `${MIGRATION_KEY_PREFIX}_${apiService.getUserStorageSuffix()}_${signature}`;
};

const buildImportMessages = (legacyMessages) => {
  return legacyMessages.map((item) => ({
    role: item.type === "user" ? "user" : "assistant",
    content:
      item.type === "ai"
        ? stripModelProtocolMarkers(item.content)
        : item.content,
    show_content: item.type === "user" ? item.content : undefined,
    reasoning_content:
      item.type === "ai"
        ? stripModelProtocolMarkers(item.reasoning)
        : undefined,
    localCreatedAt: item.timestamp,
  }));
};

const chunkArray = (list, size) => {
  const result = [];
  for (let index = 0; index < list.length; index += size) {
    result.push(list.slice(index, index + size));
  }
  return result;
};

const migrateLegacyHistory = async () => {
  const legacyMessages = getLegacyMessages();
  if (legacyMessages.length <= 0) {
    return "";
  }

  const signature = getHistorySignature(legacyMessages);
  const migrationKey = getMigrationStorageKey(signature);
  const migratedInfo = safeLocalStorageGet(migrationKey);
  if (migratedInfo) {
    try {
      return JSON.parse(migratedInfo)?.conversationId || "";
    } catch (error) {
      return "";
    }
  }

  migrationState.value = "info";
  migrationNotice.value = "正在迁移浏览器本地旧记录...";
  let migrationConversation = null;
  try {
    migrationConversation = await apiService.createConversation(
      `${apiService.CONVERSATION_NAME_PREFIX}本地历史迁移 ${formatTime(
        new Date(),
      )}`,
    );
    let insertedCount = 0;
    const importChunks = chunkArray(buildImportMessages(legacyMessages), 250);
    for (const chunk of importChunks) {
      const importResult = await apiService.importMessages(
        migrationConversation._id,
        chunk,
      );
      insertedCount += importResult?.insertedCount || chunk.length;
    }
    safeLocalStorageSet(
      migrationKey,
      JSON.stringify({
        conversationId: migrationConversation._id,
        count: insertedCount,
        migratedAt: new Date().toISOString(),
      }),
    );
    migrationState.value = "success";
    migrationNotice.value = `已迁移 ${legacyMessages.length} 条本地旧记录`;
    setTimeout(() => {
      if (migrationState.value === "success") {
        migrationNotice.value = "";
      }
    }, 5000);
    return migrationConversation._id;
  } catch (error) {
    console.error("Legacy history migration failed:", error);
    if (migrationConversation?._id) {
      await apiService
        .removeConversation(migrationConversation._id)
        .catch(() => undefined);
    }
    migrationState.value = "error";
    migrationNotice.value = "本地旧记录暂未迁移，原浏览器记录仍保留";
    return "";
  }
};

const loadLegacyHistoryForPreview = () => {
  const loginNotice = "登录后可继续提问，并自动同步聊天记录。";
  const legacyMessages = getLegacyMessages();
  if (legacyMessages.length > 0) {
    messages.value = legacyMessages;
    migrationState.value = "info";
    migrationNotice.value = loginNotice;
    scrollToBottom();
    return;
  }

  messages.value = [];
  migrationState.value = "info";
  migrationNotice.value = loginNotice;
};

const savePendingLoginDraft = () => {
  if (typeof window === "undefined") {
    return;
  }
  const draft = `${userInput.value || ""}`;
  if (!draft.trim()) {
    return;
  }
  try {
    window.localStorage.setItem(
      PENDING_LOGIN_DRAFT_KEY,
      JSON.stringify({
        text: draft,
        savedAt: Date.now(),
      }),
    );
  } catch (error) {
    // 本地存储不可用时，不阻塞登录流程。
  }
};

const clearPendingLoginDraft = () => {
  if (typeof window === "undefined") {
    return;
  }
  try {
    window.localStorage.removeItem(PENDING_LOGIN_DRAFT_KEY);
  } catch (error) {
    // 忽略本地存储清理失败。
  }
};

const restorePendingLoginDraftIfNeeded = () => {
  if (!isAuthenticated.value || typeof window === "undefined") {
    return false;
  }
  let payload = null;
  try {
    payload = JSON.parse(
      window.localStorage.getItem(PENDING_LOGIN_DRAFT_KEY) || "null",
    );
  } catch (error) {
    clearPendingLoginDraft();
    return false;
  }
  const draft = `${payload?.text || ""}`;
  if (!draft.trim()) {
    clearPendingLoginDraft();
    return false;
  }

  clearPendingLoginDraft();
  activeConversationId.value = "";
  messages.value = [];
  userInput.value = draft;
  apiService.resetConversation();
  nextTick(() => {
    autoResize();
    inputTextarea.value?.focus?.();
  });
  return true;
};

const refreshConversations = async (options = {}) => {
  if (!isAuthenticated.value) {
    loadLegacyHistoryForPreview();
    return;
  }

  historyLoading.value = true;
  try {
    const res = await apiService.listConversations({
      pageNo: 1,
      pageSize: 80,
      type: "model",
      name: "特教求索",
    });
    const rows = (res?.rows || [])
      .filter((item) => `${item.name || ""}`.includes("特教求索"))
      .sort(
        (left, right) =>
          parseTimestamp(right.updatedAt || right.createdAt) -
          parseTimestamp(left.updatedAt || left.createdAt),
      );
    conversationList.value = rows;

    if (options.keepCurrent) {
      return;
    }

    if (
      !options.preferredConversationId &&
      restorePendingLoginDraftIfNeeded()
    ) {
      return;
    }

    const preferredId =
      options.preferredConversationId ||
      activeConversationId.value ||
      apiService.getStoredConversationId();
    const target = rows.find((item) => item._id === preferredId) || rows[0];
    if (target && target._id !== activeConversationId.value) {
      await selectConversation(target._id, { skipRefresh: true });
    } else if (!target) {
      activeConversationId.value = "";
      messages.value = [];
      apiService.resetConversation();
    }
  } catch (error) {
    handleError(error);
  } finally {
    historyLoading.value = false;
  }
};

const initializeConversations = async () => {
  if (!isAuthenticated.value) {
    loadLegacyHistoryForPreview();
    return;
  }

  const migratedConversationId = await migrateLegacyHistory();
  await refreshConversations({
    preferredConversationId:
      migratedConversationId || apiService.getStoredConversationId(),
  });
};

const selectConversation = async (conversationId, options = {}) => {
  if (
    !conversationId ||
    (conversationId === activeConversationId.value && !options.force)
  ) {
    historyPanelOpen.value = false;
    return;
  }

  messagesLoading.value = true;
  try {
    activeConversationId.value = conversationId;
    apiService.setActiveConversation(conversationId);
    const res = await apiService.listConversationMessages(conversationId);
    messages.value = (res?.rows || []).map(toLocalMessage);
    shouldAutoScroll.value = true;
    scrollToBottom();
    historyPanelOpen.value = false;
  } catch (error) {
    handleError(error);
  } finally {
    messagesLoading.value = false;
  }
};

const startNewConversation = () => {
  activeConversationId.value = "";
  messages.value = [];
  apiService.resetConversation();
  resetUploadedFiles();
  historyPanelOpen.value = false;
  nextTick(() => inputTextarea.value?.focus());
};

const upsertConversation = (conversation) => {
  if (!conversation?._id) {
    return;
  }
  const existsIndex = conversationList.value.findIndex(
    (item) => item._id === conversation._id,
  );
  if (existsIndex >= 0) {
    conversationList.value[existsIndex] = {
      ...conversationList.value[existsIndex],
      ...conversation,
      updatedAt: new Date().toISOString(),
    };
  } else {
    conversationList.value.unshift(conversation);
  }
};

const createConversationForQuestion = async (question) => {
  if (activeConversationId.value) {
    return activeConversationId.value;
  }
  const cleanQuestion = question.replace(/\s+/g, " ").trim();
  const nameSuffix =
    cleanQuestion.length > 24
      ? `${cleanQuestion.slice(0, 24)}...`
      : cleanQuestion || "新会话";
  const conversation = await apiService.createConversation(
    `${apiService.CONVERSATION_NAME_PREFIX}${nameSuffix}`,
  );
  activeConversationId.value = conversation._id;
  upsertConversation(conversation);
  return conversation._id;
};

const getMessageTimestamp = (msg) => {
  if (!msg) return 0;
  return msg.timestamp || msg.id || 0;
};

const getDistanceFromBottom = () => {
  if (!messagesContainer.value) return 0;
  const { scrollTop, scrollHeight, clientHeight } = messagesContainer.value;
  return Math.max(0, scrollHeight - scrollTop - clientHeight);
};

const isNearBottom = () => {
  return getDistanceFromBottom() <= AUTO_SCROLL_BOTTOM_THRESHOLD;
};

const cancelPendingAutoScroll = () => {
  if (pendingAutoScrollFrame) {
    window.cancelAnimationFrame(pendingAutoScrollFrame);
    pendingAutoScrollFrame = null;
  }
};

const scrollToBottom = ({ force = false } = {}) => {
  if (!force && !shouldAutoScroll.value) return;
  nextTick(() => {
    const container = messagesContainer.value;
    if (!container || (!force && !shouldAutoScroll.value)) return;
    cancelPendingAutoScroll();
    pendingAutoScrollFrame = window.requestAnimationFrame(() => {
      pendingAutoScrollFrame = null;
      if (!container || (!force && !shouldAutoScroll.value)) return;
      container.scrollTop = container.scrollHeight;
    });
  });
};

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const splitTextToTypeUnits = (text = "") => {
  const rawText = `${text || ""}`;
  if (!rawText) return [];

  if (typeof Intl !== "undefined" && Intl.Segmenter) {
    try {
      return Array.from(
        new Intl.Segmenter("zh-CN", { granularity: "grapheme" }).segment(
          rawText,
        ),
        (item) => item.segment,
      );
    } catch (error) {
      // Fall back to code points below.
    }
  }

  return Array.from(rawText);
};

const getTypewriterDelay = (queueLength) => {
  if (queueLength > TYPEWRITER_SPRINT_QUEUE_SIZE)
    return TYPEWRITER_SPRINT_DELAY_MS;
  if (queueLength > TYPEWRITER_FAST_QUEUE_SIZE) return TYPEWRITER_FAST_DELAY_MS;
  return TYPEWRITER_DELAY_MS;
};

const syncLocalMessage = (targetMessage, forceScroll = false) => {
  const index = messages.value.findIndex((m) => m.id === targetMessage.id);
  if (index !== -1) {
    messages.value[index] = { ...targetMessage };
    scrollToBottom(forceScroll);
  }
};

const processStreamData = async (reader, decoder, currentMessage) => {
  let stopTypewriterQueue = null;

  try {
    let isFirstChunk = true;
    let isReasoningComplete = false;
    let buffer = "";
    let typewriterRunning = false;
    let typewriterStopped = false;
    let typewriterTask = Promise.resolve();
    const textQueue = [];

    const updateCurrentMessage = () => {
      const index = messages.value.findIndex((m) => m.id === currentMessage.id);
      if (index !== -1) {
        messages.value[index] = {
          ...currentMessage,
          thinkingTime: finalThinkingTime.value,
        };
        scrollToBottom();
      }
    };

    const runTypewriter = async () => {
      if (typewriterRunning) return;

      typewriterRunning = true;
      try {
        while (!typewriterStopped && textQueue.length > 0) {
          const nextItem = textQueue.shift();
          if (nextItem?.char) {
            currentMessage[nextItem.field] = `${
              currentMessage[nextItem.field] || ""
            }${nextItem.char}`;
            updateCurrentMessage();
          }
          await sleep(getTypewriterDelay(textQueue.length));
        }
      } finally {
        typewriterRunning = false;
      }
    };

    const ensureTypewriter = () => {
      if (!typewriterRunning) {
        typewriterTask = runTypewriter();
      }
      return typewriterTask;
    };

    const enqueueText = (field, content) => {
      textQueue.push(
        ...splitTextToTypeUnits(content).map((char) => ({ field, char })),
      );
      ensureTypewriter();
    };

    const waitForTypewriter = async () => {
      while (
        !typewriterStopped &&
        (typewriterRunning || textQueue.length > 0)
      ) {
        if (!typewriterRunning && textQueue.length > 0) {
          ensureTypewriter();
        }
        await typewriterTask;
      }
    };

    stopTypewriterQueue = () => {
      typewriterStopped = true;
      textQueue.length = 0;
    };

    if (currentStream.value) {
      currentStream.value.stopTyping = stopTypewriterQueue;
    }

    const markFirstVisibleChunk = (stopAtThisChunk = false) => {
      if (isFirstChunk) {
        isFirstChunk = false;
      }
      if (stopAtThisChunk && isThinking.value) {
        finalThinkingTime.value = getThinkingElapsedSeconds();
        stopThinkingTimer();
      }
    };

    const handleReasoningContent = (reasoningContent) => {
      let nextReasoningContent = reasoningContent;
      if (!nextReasoningContent || nextReasoningContent === "null") return;
      if (currentMessage.reasoning.includes("**联网搜索过程**")) {
        nextReasoningContent = nextReasoningContent.replace(
          /^\*\*联网搜索过程\*\*\n?/,
          "",
        );
      }
      if (!nextReasoningContent) return;
      markFirstVisibleChunk(false);
      currentMessage.reasoning += nextReasoningContent;
      if (!isReasoningComplete) {
        currentMessage.isReasoningPreview = true;
        enqueueText("reasoningPreview", nextReasoningContent);
      }
      updateCurrentMessage();
    };

    const handleAnswerContent = (content) => {
      if (!content || content === "null") return;
      markFirstVisibleChunk(true);
      if (!isReasoningComplete) {
        isReasoningComplete = true;
        textQueue.length = 0;
        currentMessage.reasoningPreview = "";
        currentMessage.content = "";
        currentMessage.isReasoningPreview = false;
        updateCurrentMessage();
      }
      enqueueText("content", content);
    };

    const consumeLine = async (line) => {
      if (!line.startsWith("data:")) {
        return;
      }

      const payload = line.replace(/^data:\s?/, "").trim();
      if (!payload || payload === "[DONE]") {
        return;
      }

      const data = JSON.parse(payload);
      if (data._status === "error") {
        throw new Error(data.errorMsg || "请求失败");
      }
      if (data._messageId) {
        currentMessage.serverId = data._messageId;
      }

      if (data._tejiaoUi?.version === 1) {
        currentMessage.tejiaoUi = data._tejiaoUi;
        updateCurrentMessage();
      }

      const choice = data.choices?.[0] || {};
      const delta = choice.delta || choice.message || data;
      const reasoningContent = stripModelProtocolMarkers(
        delta.reasoning_content || data.reasoning_content || "",
      );
      const content = stripModelProtocolMarkers(
        delta.content || data.content || "",
      );

      handleReasoningContent(reasoningContent);
      handleAnswerContent(content);
    };

    while (true) {
      const { done, value } = await reader.read();
      if (done) {
        if (buffer.trim()) {
          await consumeLine(buffer.trim());
        }
        await waitForTypewriter();
        stopThinkingTimer(true);
        break;
      }

      buffer += decoder.decode(value, { stream: true }).replace(/\r\n/g, "\n");

      let lineEndIndex = buffer.indexOf("\n");
      while (lineEndIndex >= 0) {
        const line = buffer.slice(0, lineEndIndex).trim();
        buffer = buffer.slice(lineEndIndex + 1);
        if (line) {
          await consumeLine(line);
        }
        lineEndIndex = buffer.indexOf("\n");
      }
    }
  } catch (error) {
    stopTypewriterQueue?.();
    if (error.name === "AbortError") {
      isStreaming.value = false;
      currentStream.value = null;
      stopThinkingTimer(true);
      const index = messages.value.findIndex((m) => m.id === currentMessage.id);
      if (index !== -1) {
        messages.value[index] = {
          ...currentMessage,
          thinkingTime: finalThinkingTime.value,
        };
      }
    } else {
      console.error("Stream error:", error);
      isStreaming.value = false;
      currentStream.value = null;
      stopThinkingTimer(true);
      currentMessage.reasoningPreview = "";
      currentMessage.isReasoningPreview = false;
      handleError(error, currentMessage);
    }
  } finally {
    if (currentStream.value?.stopTyping === stopTypewriterQueue) {
      currentStream.value.stopTyping = null;
    }
  }
};

const buildContextMessages = () => {
  const contextMessages = [];
  const cutoff = Date.now() - CONTEXT_RETENTION_MS;
  const recentMessages = messages.value.filter(
    (msg) => getMessageTimestamp(msg) >= cutoff,
  );

  contextMessages.push({
    role: "system",
    content:
      "你是一名专业的特殊教育从业者，有扎实的特殊教育理论知识，并且在中国有丰富的特殊教育实践经验。关于特殊教育，有些问题希望听听你的建议。思考阶段不要输出正文内容；如果已经思考完毕，请直接输出最终内容。思考过程中不要复述或输出用户对你的要求，而要围绕用户提问本身进行分析，思考如何提供更高质量的回复。",
  });

  contextMessages.push(
    ...recentMessages
      .filter((msg) => msg.type !== "ai" || msg.content.trim() !== "")
      .map((msg) => ({
        role: msg.type === "user" ? "user" : "assistant",
        content:
          msg.type === "ai"
            ? stripModelProtocolMarkers(msg.content)
            : msg.requestContent || msg.content,
        show_content: msg.type === "user" ? msg.content : undefined,
      })),
  );

  return contextMessages;
};

const streamAiResponseForQuestion = async (
  question,
  aiMessage,
  targetConversationId,
  forcedWebSearchEnabled = null,
  uiAction = null,
) => {
  isStreaming.value = true;
  startThinking();
  scrollToBottom();

  const usedWebSearch =
    forcedWebSearchEnabled === null
      ? webSearchEnabled.value
      : !!forcedWebSearchEnabled;
  aiMessage.usedWebSearch = usedWebSearch;

  const { stream, stop, conversationId } = await apiService.streamChat(
    buildContextMessages(),
    {
      conversationId: targetConversationId,
      webSearchEnabled: usedWebSearch,
      uiAction,
    },
  );

  activeConversationId.value = conversationId;
  currentStream.value = { stop };
  const decoder = new TextDecoder();
  await processStreamData(stream, decoder, aiMessage);
  if (usedWebSearch) {
    webSearchEnabled.value = false;
  }
  await refreshConversations({ keepCurrent: true });

  try {
    await logChat(question, {
      reasoning: stripModelProtocolMarkers(aiMessage.reasoning),
      reasoning_content: stripModelProtocolMarkers(aiMessage.reasoning),
      content: stripModelProtocolMarkers(aiMessage.content),
      thinking_time: finalThinkingTime.value,
    });
  } catch (e) {
    console.error("Logging error:", e);
  }
};

const sendMessage = async (uiAction = null) => {
  if ((!uiAction && !canSend.value) || isStreaming.value || messagesLoading.value)
    return;
  if (!isAuthenticated.value) {
    savePendingLoginDraft();
    toast.value?.showToast({
      message: "请先登录，登录后会保留刚才输入的内容。",
      type: "info",
    });
    emit("login");
    return;
  }

  const question = uiAction?.content || userInput.value.trim() || "请分析上传的图片或文件。";
  const requestContent = uiAction ? question : buildUserContent(question, fileContexts.value);
  shouldAutoScroll.value = true;
  isStreaming.value = true;

  try {
    const targetConversationId = await createConversationForQuestion(question);
    const timestamp = Date.now();
    const userMessage = {
      id: `local-user-${timestamp}`,
      type: "user",
      content: question,
      requestContent,
      images: getMessageImages(requestContent),
      timestamp,
    };

    const aiMessage = {
      id: `local-ai-${timestamp}`,
      type: "ai",
      reasoning: "",
      reasoningPreview: "",
      isReasoningPreview: false,
      content: "",
      timestamp: timestamp + 1,
    };

    messages.value.push(userMessage);
    messages.value.push(aiMessage);

    if (!uiAction) {
      userInput.value = "";
      resetUploadedFiles();
    }
    await streamAiResponseForQuestion(
      question,
      aiMessage,
      targetConversationId,
      null,
      uiAction,
    );
  } catch (error) {
    handleError(error, messages.value[messages.value.length - 1]);
  } finally {
    isStreaming.value = false;
    currentStream.value = null;
  }
};

const regenerateAiMessage = async (aiMessage) => {
  if (isStreaming.value || messagesLoading.value || !aiMessage) return;
  if (!isAuthenticated.value) {
    toast.value?.showToast({
      message: "请先登录后再重新生成。",
      type: "info",
    });
    emit("login");
    return;
  }

  const aiIndex = messages.value.findIndex((m) => m.id === aiMessage.id);
  if (aiIndex <= 0) return;
  const userMessage = [...messages.value.slice(0, aiIndex)]
    .reverse()
    .find((m) => m.type === "user");
  if (!userMessage?.content?.trim()) {
    toast.value?.showToast({
      message: "没有找到可重新生成的问题。",
      type: "error",
    });
    return;
  }

  const timestamp = Date.now();
  const nextAiMessage = {
    id: `local-ai-regenerate-${timestamp}`,
    type: "ai",
    reasoning: "",
    reasoningPreview: "",
    isReasoningPreview: false,
    usedWebSearch: !!aiMessage.usedWebSearch,
    content: "",
    timestamp,
  };

  shouldAutoScroll.value = true;
  messages.value.splice(
    aiIndex,
    messages.value.length - aiIndex,
    nextAiMessage,
  );

  try {
    const shouldUseWebSearch = aiMessage.usedWebSearch === true;
    await streamAiResponseForQuestion(
      userMessage.content.trim(),
      nextAiMessage,
      activeConversationId.value,
      shouldUseWebSearch,
    );
  } catch (error) {
    handleError(error, nextAiMessage);
  } finally {
    isStreaming.value = false;
    currentStream.value = null;
  }
};

const stopResponse = () => {
  if (currentStream.value) {
    currentStream.value.stopTyping?.();
    currentStream.value.stop();
    currentStream.value = null;
    isStreaming.value = false;
    stopThinkingTimer();
    toast.value?.showToast({
      message: "已停止响应",
      type: "info",
    });
  }
};

const getFriendlyErrorMessage = (error) => {
  const message = `${error?.message || error?.msg || ""}`.trim();
  if (!message) {
    return "抱歉，发生了错误，请稍后重试。";
  }
  if (
    error?.isAuthError ||
    message.includes("登录") ||
    message.includes("token")
  ) {
    return message;
  }
  if (
    message.startsWith("图片") ||
    message.startsWith("每次最多上传") ||
    message.startsWith("当前对话附件较多") ||
    message === "当前模型服务尚未配置，请联系管理员"
  ) {
    return message;
  }
  return "抱歉，发生了错误，请稍后重试。";
};

const handleError = (error, currentMessage) => {
  console.error("Chat error:", error);
  const friendlyMessage = getFriendlyErrorMessage(error);

  if (error?.isAuthError) {
    toast.value?.showToast({
      message: friendlyMessage,
      type: "error",
    });
    apiService.jumpToLogin();
    return;
  }

  if (currentMessage) {
    const targetMessage = currentMessage;
    targetMessage.reasoningPreview = "";
    targetMessage.isReasoningPreview = false;
    targetMessage.content = friendlyMessage;
    const index = messages.value.findIndex((m) => m.id === targetMessage.id);
    if (index !== -1) {
      messages.value[index] = { ...targetMessage };
    }
  }

  toast.value?.showToast({
    message: friendlyMessage,
    type: "error",
  });
};

const updateFileContexts = (newFileContexts) => {
  fileContexts.value = (newFileContexts || []).map((f) => ({ ...f }));
};
</script>

<style lang="scss" scoped>
@import "../styles/variables.scss";

.attachment-status {
  margin: 6px auto 0;
  max-width: 1120px;
  font-size: 12px;
  color: #b42318;
}

.chat-workspace {
  position: relative;
  display: grid;
  grid-template-columns: 300px minmax(0, 1fr);
  gap: 0;
  height: 100%;
  max-height: 100%;
  min-height: 0;
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    inset: 0 0 auto;
    height: 3px;
    z-index: 8;
    background: linear-gradient(90deg, #d5bd87, #b6cde4 30%, transparent 80%);
    pointer-events: none;
  }

  @media (max-width: 960px) {
    display: block;
  }
}

textarea,
input,
select {
  font-size: 16px;
}

@media (max-width: 768px) {
  textarea,
  input,
  select {
    font-size: 16px !important;
  }
}

.history-panel {
  height: 100%;
  background: #eef4fa;
  border: 0;
  border-right: 1px solid #d7e1eb;
  border-radius: 0;
  box-shadow: none;
  padding: 24px 18px 18px;
  display: flex;
  flex-direction: column;
  min-width: 0;
  min-height: 0;
  overflow: hidden;

  @media (max-width: 960px) {
    position: fixed;
    top: 0;
    left: 0;
    bottom: 0;
    width: min(86vw, 320px);
    height: 100vh;
    border-radius: 0;
    z-index: 80;
    transform: translateX(-104%);
    transition: transform 0.2s ease;

    &.open {
      transform: translateX(0);
    }
  }
}

.sidebar-brand {
  margin-bottom: 20px;
  padding: 3px 4px 20px;
  border-bottom: 1px solid #d7e1eb;

  h1 {
    color: $heading-color;
    font-size: 1.56rem;
    line-height: 1.06;
    font-weight: 820;
    letter-spacing: 0;
    margin: 0;
  }

  p {
    max-width: 220px;
    color: $muted-text-color;
    font-size: 0.86rem;
    line-height: 1.55;
    margin: 9px 0 0;
  }
}

.sidebar-heading {
  display: flex;
  align-items: center;
  gap: 10px;
}

.brand-mark {
  display: grid;
  place-items: center;
  width: 34px;
  height: 34px;
  flex-shrink: 0;
  border: 1px solid #bed0e6;
  border-radius: 10px;
  background: $accent-soft-color;
  color: $primary-color;

  svg {
    fill: none;
    stroke: currentColor;
    stroke-width: 1.5;
    stroke-linecap: round;
    stroke-linejoin: round;
  }
}

.history-head {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: $spacing-sm;
  margin-bottom: 8px;
}

.icon-button {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  border: 0;
  background: transparent;
  color: $primary-color;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;

  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }

  &:not(:disabled):hover {
    background: rgba($primary-color, 0.08);
  }
}

.new-chat-button {
  min-height: 42px;
  flex-shrink: 0;
  border: none;
  border-radius: 10px;
  background: $primary-color;
  color: #fff;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: $spacing-xs;
  font-weight: 600;
  margin-bottom: 16px;
  box-shadow: 0 8px 18px rgba($primary-color, 0.11);

  &:not(:disabled):hover {
    background: $hover-color;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
}

.migration-notice {
  border-radius: 8px;
  padding: 9px 10px;
  font-size: 0.86rem;
  line-height: 1.45;
  margin-bottom: $spacing-md;
  background: #eef6ff;
  color: #2457a6;
  border: 1px solid #cfe1fb;

  &.success {
    background: #f0f7ff;
    color: $primary-color;
    border-color: #c7dcff;
  }

  &.error {
    background: #fff7ed;
    color: #c2410c;
    border-color: #fed7aa;
  }
}

.history-label {
  color: $muted-text-color;
  font-size: 11px;
  letter-spacing: 0.08em;
  margin: 8px 10px 7px;
}

.history-list {
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 3px;
  padding-right: 2px;
  min-height: 0;
  flex: 1;
}

.history-empty {
  color: #64748b;
  font-size: 0.9rem;
  padding: $spacing-md $spacing-sm;
  text-align: center;
}

.history-item {
  position: relative;
  border: 1px solid transparent;
  background: transparent;
  border-radius: 10px;
  cursor: pointer;
  padding: 11px 12px;
  text-align: left;
  transition: background-color 0.18s ease, border-color 0.18s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.72);
    border-color: rgba(203, 213, 225, 0.72);
  }

  &.active {
    background: #ffffff;
    border-color: #bed0e6;
    box-shadow: 0 4px 12px rgba(33, 62, 90, 0.06);

    &::before {
      content: "";
      position: absolute;
      left: 0;
      top: 16px;
      bottom: 16px;
      width: 3px;
      border-radius: 0 3px 3px 0;
      background: $primary-color;
      pointer-events: none;
    }
  }
}

.history-title,
.history-meta {
  display: block;
}

.history-title {
  color: #23364d;
  font-size: 0.91rem;
  line-height: 1.35;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.history-meta {
  color: $muted-text-color;
  font-size: 0.76rem;
  margin-top: 4px;
}

.sidebar-footer {
  border-top: 1px solid #d7e1eb;
  padding-top: 14px;
  margin-top: 14px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.agent-link {
  width: 100%;
  min-height: 42px;
  border: 1px solid #bed0e6;
  border-radius: 10px;
  background: $accent-soft-color;
  color: $primary-color;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  text-decoration: none;
  font-size: 0.92rem;
  font-weight: 650;
  padding: 0 12px;
  transition: transform 0.18s ease, box-shadow 0.18s ease,
    border-color 0.18s ease;

  &:hover {
    border-color: $primary-color;
    background: #ffffff;
  }
}

.account-trigger {
  width: 100%;
  min-height: 38px;
  border: 0;
  border-radius: 8px;
  background: transparent;
  color: #31445b;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: space-between;
  text-decoration: none;
  font-size: 0.9rem;
  font-weight: 650;
  padding: 0 10px;
  transition: background-color 0.18s ease, color 0.18s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.72);
    color: $primary-color;
  }
}

.account-area {
  position: relative;
  z-index: 2;
}

.account-row {
  min-height: 40px;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.54);
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 0 10px;
}

.account-name {
  color: #31445b;
  font-size: 0.9rem;
  font-weight: 650;
  min-width: 0;
}

.logout-inline-button {
  position: static;
  flex: 0 0 auto;
  margin-left: auto;
  min-height: 30px;
  border: 1px solid rgba(180, 35, 24, 0.18);
  border-radius: 7px;
  background: #fff;
  color: #b42318;
  cursor: pointer;
  font-size: 0.84rem;
  font-weight: 650;
  padding: 0 9px;
  opacity: 0;
  pointer-events: none;
  white-space: nowrap;
  transform: translateX(4px);
  box-shadow: 0 8px 18px rgba(15, 23, 42, 0.08);
  transition: opacity 0.18s ease, transform 0.18s ease,
    background-color 0.18s ease, border-color 0.18s ease;

  &:hover {
    background: #fff5f4;
    border-color: rgba(180, 35, 24, 0.32);
  }
}

.account-area:hover .logout-inline-button,
.account-area:focus-within .logout-inline-button {
  opacity: 1;
  pointer-events: auto;
  transform: translateX(0);
}

.chat-container {
  display: grid;
  grid-template-rows: minmax(0, 1fr) auto;
  height: 100%;
  max-height: 100%;
  min-height: 0;
  min-width: 0;
  background: #f6fafe;
  border: 0;
  border-radius: 0;
  box-shadow: none;
  overflow: hidden;
  margin: 0;
  position: relative;
  isolation: isolate;

  &::before {
    content: "";
    position: absolute;
    inset: 0;
    z-index: -1;
    pointer-events: none;
    background:
      url("../assets/dawn-background.svg") center / cover no-repeat,
      radial-gradient(ellipse 52% 48% at 99% 2%, #f7e8cba8, transparent 80%),
      radial-gradient(ellipse 50% 65% at 98% 25%, #c5dff4c2, transparent 75%),
      radial-gradient(ellipse 48% 40% at 0% 100%, #c7e0f085, transparent 90%),
      linear-gradient(145deg, #f7fbff, #fbfcfd 65%, #edf5fb);
  }

  @media (max-width: 960px) {
    height: 100%;
    margin: 0;
    border-radius: 0;
    border: none;
    box-shadow: none;
  }
}

.mobile-sidebar-button {
  display: none;
  position: absolute;
  top: 12px;
  left: 12px;
  z-index: 10;
  width: 36px;
  height: 36px;
  border: 0;
  border-radius: 8px;
  background: $accent-soft-color;
  color: $primary-color;
  align-items: center;
  justify-content: center;
  cursor: pointer;

  @media (max-width: 960px) {
    display: inline-flex;
  }
}

.messages-container {
  min-height: 0;
  max-height: 100%;
  overflow-y: auto;
  padding: 30px 34px;
  background: transparent;
  scroll-behavior: smooth;
  overscroll-behavior: contain;

  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background-color: rgba(148, 163, 184, 0.45);
    border-radius: 4px;
  }

  @media (max-width: 960px) {
    padding-top: 58px;
  }

  @media (max-width: 768px) {
    padding: 58px 12px 14px;
  }
}

.center-state {
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: $spacing-sm;
  color: #64748b;
}

.small-spinner {
  width: 22px;
  height: 22px;
  border: 2px solid rgba($primary-color, 0.18);
  border-top-color: $primary-color;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

.input-container {
  min-height: 0;
  min-width: 0;
  padding: 6px 24px 10px;
  border-top: 0;
  background: linear-gradient(transparent, #f5f9fd 60%);
  position: relative;
  z-index: 2;
  overflow: visible;

  @media (max-width: 768px) {
    padding: 6px 8px calc(6px + env(safe-area-inset-bottom, 0px));
  }
}

.search-toggle {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  height: 44px;
  padding: 0 8px 0 10px;
  border: 0;
  border-radius: 999px;
  background: transparent;
  color: #1f2937;
  font-size: 0.92rem;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.18s ease, color 0.18s ease;

  &:not(:disabled):hover {
    color: $primary-color;
    background: #f2f5f9;
  }

  &.active {
    color: $primary-color;
  }

  &:disabled {
    opacity: 0.56;
    cursor: not-allowed;
  }
}

.search-toggle-label {
  white-space: nowrap;
}

.search-switch {
  width: 34px;
  height: 20px;
  border-radius: 999px;
  background: #d8dee8;
  display: inline-flex;
  align-items: center;
  padding: 2px;
  transition: background-color 0.18s ease;

  span {
    width: 16px;
    height: 16px;
    border-radius: 50%;
    background: #ffffff;
    box-shadow: 0 1px 3px rgba(15, 23, 42, 0.2);
    transition: transform 0.18s ease;
  }
}

.search-toggle.active .search-switch {
  background: $primary-color;

  span {
    transform: translateX(14px);
  }
}

.input-row {
  display: flex;
  align-items: stretch;
}

.textarea-wrapper {
  flex: 1;
  min-width: 0;
  display: flex;
}

textarea {
  width: 100%;
  min-height: 28px;
  max-height: min(192px, calc(var(--vh, 1vh) * 30));
  box-sizing: border-box;
  padding: 2px 4px;
  margin: 0;
  border: 0;
  border-radius: 0;
  resize: none;
  font-family: inherit;
  font-size: 1rem;
  line-height: 24px;
  overflow-y: auto;
  background: transparent;
  color: #1f2937;

  &:focus {
    outline: none;
  }

  &:disabled {
    background: transparent;
    color: #94a3b8;
  }

  &::placeholder {
    color: $muted-text-color;
  }
}

.send-button {
  width: 44px;
  height: 44px;
  min-height: 44px;
  flex: 0 0 44px;
  align-self: center;
  margin-left: auto;
  padding: 0;
  border-radius: 50%;
  font-weight: 600;
  transition: all 0.2s ease;
  border: none;
  background: $primary-color;
  color: white;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 8px 18px rgba(15, 23, 42, 0.16);

  &:not(:disabled):hover {
    background: $hover-color;
    transform: translateY(-1px);
  }

  &:disabled {
    background: #e2e8f0;
    color: #94a3b8;
    cursor: not-allowed;
  }

  &.stop-button {
    background: #dc2626;
  }
}

@media (max-width: 768px) {
  .search-toggle {
    flex: 0 0 auto;
    padding: 0 6px 0 8px;
    font-size: 0.84rem;
  }
}

.welcome-container {
  width: min(100%, 720px);
  min-height: min(560px, calc(100% - 24px));
  margin: 0 auto;
  padding: 42px 12px 28px;
  display: flex;
  flex-direction: column;
  justify-content: center;

  @media (max-width: 768px) {
    min-height: calc(100% - 18px);
    justify-content: flex-start;
    padding: 20px 0 10px;
  }
}

.welcome-title {
  color: $heading-color;
  font-size: 2.48rem;
  line-height: 1.08;
  font-weight: 780;
  letter-spacing: 0;
  text-align: center;
  margin: 0 0 16px;

  @media (max-width: 768px) {
    font-size: 2.08rem;
    margin-bottom: 14px;
  }
}

.welcome-message {
  position: relative;
  color: #172033;
  font-size: 1.05rem;
  line-height: 1.82;
  margin-bottom: 30px;
  padding: 22px 24px;
  border: 1px solid rgba(226, 232, 240, 0.92);
  border-radius: 8px;
  background: #ffffff;
  box-shadow: 0 14px 36px rgba(15, 23, 42, 0.05);

  &::before {
    content: "";
    position: absolute;
    left: 0;
    top: 22px;
    bottom: 22px;
    width: 3px;
    border-radius: 0 999px 999px 0;
    background: $primary-color;
    pointer-events: none;
  }

  @media (max-width: 768px) {
    margin-bottom: 22px;
    padding: 18px 19px;
    border-radius: 8px;
    font-size: 0.98rem;
    line-height: 1.76;

    &::before {
      top: 18px;
      bottom: 18px;
    }
  }
}

.example-questions {
  max-width: 720px;

  @media (max-width: 768px) {
    padding: 0;
  }
}

.question-title {
  color: $muted-text-color;
  margin-bottom: 11px;
  padding-left: 2px;
  font-size: 0.92rem;
  font-weight: 650;
  letter-spacing: 0;
}

.questions-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.example-question {
  min-height: 60px;
  background: #ffffff;
  color: $primary-color;
  padding: 16px 18px;
  border-radius: 8px;
  cursor: pointer;
  transition: transform 0.16s ease, box-shadow 0.16s ease,
    border-color 0.16s ease, background-color 0.16s ease;
  font-size: 0.94rem;
  border: 1px solid rgba(226, 232, 240, 0.94);
  text-align: left;
  line-height: 1.58;
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.035);
  display: flex;
  align-items: center;

  &:hover {
    background: #fbfdff;
    border-color: rgba(37, 93, 168, 0.3);
    box-shadow: 0 12px 30px rgba(15, 23, 42, 0.065);
    transform: translateY(-1px);
  }

  &:focus-visible {
    outline: 3px solid rgba(37, 93, 168, 0.18);
    outline-offset: 2px;
  }

  @media (max-width: 768px) {
    min-height: 0;
    border-radius: 8px;
    padding: 14px 15px;
    font-size: 0.92rem;
    line-height: 1.55;
  }
}

@media (max-width: 768px) and (max-height: 700px) {
  .messages-container {
    padding: 54px 8px 8px;
  }

  .input-container {
    padding: 4px 8px calc(6px + env(safe-area-inset-bottom, 0px));
  }
}

.mobile-history-mask {
  display: none;

  @media (max-width: 960px) {
    display: block;
    position: fixed;
    inset: 0;
    z-index: 70;
    background: rgba(15, 23, 42, 0.36);
  }
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}
</style>
